import { useScopedTranslation } from 'application/localization/useScopedTranslation';
import { CustomModal } from 'components/uikit/modal/CustomModal';
import styles from './UpdateTemplateComment.module.scss';
import { Button, ButtonColor } from 'components/uikit/buttons/Button';
import { Field } from 'components/uikit/Field';
import { TextArea } from 'components/uikit/inputs/TextArea';
import { useEffect, useMemo } from 'react';
import { useModal } from 'components/uikit/modal/useModal';
import { QueryFactory } from 'services/api';
import { useQueryClient } from '@tanstack/react-query';
import { maxCommentLength } from '../../UploadNewVersionModal/UploadNewVersionModal';
import { useAdvancedForm } from 'helpers/form/useAdvancedForm';
import { maxLengthRule } from 'helpers/form/react-hook-form-helper';

type UpdateTemplateCommentProps = {
  templateId: number;
  currentComment: string | null;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
};

export const UpdateTemplateComment: React.FC<UpdateTemplateCommentProps> = (
  props,
) => {
  const i18n = useScopedTranslation('Component.UpdateTemplateComment');
  const title = props.currentComment
    ? i18n.t('title_edit')
    : i18n.t('title_add');
  const modals = useModal();
  const queryClient = useQueryClient();

  const form = useAdvancedForm<{ comment: string }>(
    async (data) => {
      await saveComment(data.comment);
      form.reset();
      props.setIsOpen(false);
    },
    {
      defaultValues: useMemo(() => {
        return { comment: props.currentComment ?? '' };
      }, [props.currentComment]),
    },
  );

  const saveComment = async (comment: string) => {
    if (comment === props.currentComment) return;

    await QueryFactory.TemplateClient.patch(props.templateId, {
      creationComment: comment,
    });

    await queryClient.invalidateQueries({
      queryKey: QueryFactory.TemplateQuery.getVersionHistoryQueryKey(0).slice(
        0,
        1,
      ),
    });
  };

  const onClose = async () => {
    props.setIsOpen(false);
    const currentValue = form.getValues('comment');
    if (currentValue !== (props.currentComment ?? '')) {
      const isConfirmed = await modals.showConfirm({
        title: i18n.t('ConfirmSaveModal.title'),
        text: i18n.t('ConfirmSaveModal.message'),
        okButtonText: i18n.t('ConfirmSaveModal.ok_button'),
        cancelButtonText: i18n.t('ConfirmSaveModal.cancel_button'),
      });
      if (isConfirmed) await saveComment(currentValue);
    }
  };

  useEffect(() => {
    if (props.isOpen) {
      setTimeout(() => {
        form.setFocus('comment');
      });
    }
  }, [form, props.isOpen]);

  return (
    <CustomModal
      isOpen={props.isOpen}
      onClose={onClose}
      isBlocking={true}
      title={title}
    >
      <form onSubmit={form.handleSubmitDefault}>
        <div className={styles.container}>
          <div className={styles.body}>
            <Field title={i18n.t('comment_field')} className={styles.field}>
              <TextArea
                {...form.register('comment', {
                  ...maxLengthRule(maxCommentLength),
                })}
                className={styles.commentArea}
                placeholder={i18n.t('comment_placeholder')}
                errorText={form.formState.errors.comment?.message}
              />
            </Field>
          </div>

          <div className={styles.footer}>
            <Button
              className={styles.button}
              color={ButtonColor.Secondary}
              title={i18n.t('cancel_button')}
              onClick={onClose}
            />
            <Button
              className={styles.button}
              color={ButtonColor.Primary}
              type={'submit'}
              title={i18n.t('ok_button')}
            />
          </div>
        </div>
      </form>
    </CustomModal>
  );
};
